import { Box } from '@material-ui/core'
import DialogContent from '@material-ui/core/DialogContent'
import * as Sentry from '@sentry/react'
import { ClosableDialog } from 'components/controls/dialogs'
import { CircularLoading } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { editUserAttribute, publishChangesToPTInventory } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DATA_PREPARATION_ERROR,
  INVALID_PRICE,
  InventoryArticle,
  INVENTORY_TRANSACTION_CAUSES,
  PublishChangesResponse,
  toSlimInventoryArticleList,
} from 'shared'
import { PublishChangesSummary } from '../PublishChangesDialog/PublishChangesSummary'

interface SaveChangesToInventoryDialogProps {
  articles: InventoryArticle[]
  handleChangesPublished: (failed: PublishChangesResponse) => void
  open: boolean
  setOpen: (open: boolean) => void
  startPolling: (idGame: number) => void
}

export const SaveChangesToInventoryDialog = ({
  open,
  articles,
  setOpen,
  handleChangesPublished,
  startPolling,
}: SaveChangesToInventoryDialogProps): JSX.Element => {
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { setShowSpinnerLoading } = useTransactionsSpinnerStore()
  const { setStockLock } = useStockStore()

  const { t } = useTranslation()
  const { user, setUser, activeGame } = useUser()
  const { handle } = useErrorHandler()

  const handleClose = (): void => {
    setOpen(false)
  }

  const getArticlesChanged = (articles: InventoryArticle[]): InventoryArticle[] => {
    const articlesChanged: InventoryArticle[] = []
    for (let i = 0; i < articles.length; i++) {
      if (
        articles[i].selected &&
        (articles[i].newQuantity ||
          articles[i].newPrice !== articles[i].price ||
          articles[i].newComment ||
          articles[i].newCondition)
      ) {
        articlesChanged.push(articles[i])
      }
    }
    return articlesChanged
  }
  const articlesChanged = getArticlesChanged(articles)

  const handlePublish = async (): Promise<void> => {
    setLoading(true)
    try {
      const results = await publishChangesToPTInventory(
        user,
        toSlimInventoryArticleList(articlesChanged),
        false,
        INVENTORY_TRANSACTION_CAUSES.MANUAL_CHANGE
      )
      handleChangesPublished(results)

      const resp = await editUserAttribute(
        user.articlesPublishedToday + articles.length,
        'articlesPublishedToday'
      )
      setUser({ ...user!, ...resp })
      await startPolling(activeGame.idGame)

      //activate spinner visualization
      setShowSpinnerLoading(true)
      setStockLock(true)

      handleClose()
    } catch (err: any) {
      Sentry.withScope((scope) => {
        scope.setTag('component', 'SaveChangesToInventoryDialog')
        scope.setExtra('context', 'publishChangesToPTInventory')
        scope.setExtra('articles', toSlimInventoryArticleList(articlesChanged))
        scope.setExtra('articles length', articlesChanged.length)
        Sentry.captureException(err)
      })
      const status = err?.response?.status

      const backendMessage = err?.response?.data?.message

      if (status === 422) {
        if (backendMessage === INVALID_PRICE) {
          enqueueSnackbar(`${t('error.invalidPrice')}`, { variant: 'error' })
        } else if (backendMessage === DATA_PREPARATION_ERROR) {
          enqueueSnackbar(`${t('error.cannotSaveArticles')}`, { variant: 'error' })
        }
      } else if (status === 504) {
        enqueueSnackbar(`${t('error.gatewayTimeoutInventoryError')}`, { variant: 'error' })
      } else {
        handle(err)
      }
    }
    setLoading(false)
  }

  return (
    <ClosableDialog
      open={open}
      maxWidth="lg"
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      hideButton={true}
    >
      <Box width="400px" maxWidth="100%">
        <h3>{t('stockPricing.actions.publishChangesPT')}</h3>
      </Box>

      <Box>
        <DialogContent>
          {loading ? (
            <Box display="flex" padding={3} justifyContent="center">
              <CircularLoading />
            </Box>
          ) : (
            <PublishChangesSummary
              articlesChanged={articlesChanged}
              onPublishChanges={handlePublish}
              onCancelClick={handleClose}
            />
          )}
        </DialogContent>
      </Box>
    </ClosableDialog>
  )
}
